// =====================================================
// SUBSCRIPTION SYSTEM - TypeScript Types & Enums
// Created: 2026-01-13
// Phase: 1 - Foundation
// =====================================================

/**
 * Subscription Plan Types
 */
export type SubscriptionPlanName = 'trial' | 'monthly' | 'yearly' | 'setup_assisted' | 'test_1eur' | 'unlimited' | 'affiliate';

export type SubscriptionStatus =
    | 'active'       // Subscription is active and paid
    | 'cancelled'    // User cancelled, still active until expiry
    | 'expired'      // Subscription has expired
    | 'past_due'     // Payment failed, in grace period
    | 'grace_period'; // Post-expiry grace period (7 days)

/**
 * Subscription Plan (from subscription_plans table)
 */
export interface SubscriptionPlan {
    id: string;
    name: SubscriptionPlanName;
    display_name: string;
    price_cents: number; // Stripe price in cents (1990 = €19.90)
    apple_price_cents?: number | null; // Apple IAP price in cents (1999 = €19.99) - may differ due to Apple tiers
    currency: string; // 'EUR'
    duration_days: number; // 30, 365
    stripe_price_id: string | null; // Stripe Price ID (null for trial)
    apple_product_id?: string | null; // Apple Product ID (e.g. io.deskbay.app.monthly)
    features: SubscriptionFeatures;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Feature Flags per Plan
 */
export interface SubscriptionFeatures {
    qr_code: boolean;
    directory: boolean;
    analytics: boolean;
    priority_support?: boolean;
    custom_domain?: boolean; // Future feature
    advanced_booking?: boolean; // Future feature
}

/**
 * Billing provider — chi gestisce il pagamento.
 * 'stripe'  → Web / Android (default, invariato)
 * 'apple'   → iOS nativo StoreKit 2 (solo su app iOS)
 * 'manual'  → Creata manualmente da admin
 */
export type BillingProvider = 'stripe' | 'apple' | 'manual';

/**
 * Subscription (from subscriptions table)
 */
export interface Subscription {
    id: string;
    restaurant_id: string;
    plan_id: string;
    status: SubscriptionStatus;

    // Dates
    started_at: string; // ISO timestamp
    expires_at: string; // ISO timestamp
    cancelled_at: string | null;

    // Billing
    auto_renew: boolean;
    /** Chi gestisce il pagamento. Default 'stripe' per tutte le sub esistenti. */
    billing_provider: BillingProvider;
    stripe_subscription_id: string | null;
    stripe_customer_id: string | null;
    /** ID originale transazione Apple — presente solo se billing_provider = 'apple' */
    apple_original_transaction_id: string | null;

    // Grace Period
    grace_period_started_at: string | null;
    grace_period_days_used: number | null;

    // Metadata
    created_at: string;
    updated_at: string;

    // Joined data (when queried with join)
    plan?: SubscriptionPlan;
}

/**
 * Restaurant Subscription Status (from view)
 */
export interface RestaurantSubscriptionStatus {
    restaurant_id: string;
    slug: string;
    subscription_id: string | null;
    plan_name: SubscriptionPlanName | null;
    display_name: string | null;
    status: SubscriptionStatus | null;
    started_at: string | null;
    expires_at: string | null;
    auto_renew: boolean | null;
    is_active: boolean;
    days_remaining: number | null;
    grace_period_started_at: string | null;
    is_deferred_billing?: boolean; // First charge deferred to trial end
}

/**
 * Subscription Plan Pricing Constants
 */
export const SUBSCRIPTION_PRICING = {
    trial: {
        name: 'trial' as const,
        display_name: 'Trial Gratuito',
        price_cents: 0,
        price_display: '€0',
        duration_days: 90,
        stripe_price_id: null,
    },
    monthly: {
        name: 'monthly' as const,
        display_name: 'Mensile Professional',
        price_cents: 1990,
        price_display: '€19,90',
        duration_days: 30,
        stripe_price_id: process.env.VITE_STRIPE_MONTHLY_PRICE_ID || null,
    },
    yearly: {
        name: 'yearly' as const,
        display_name: 'Annuale Professional',
        price_cents: 19900, // €199/year = ~€16.58/month
        price_display: '€199',
        duration_days: 365,
        stripe_price_id: process.env.VITE_STRIPE_YEARLY_PRICE_ID || null,
        discount_percentage: 17, // 17% savings vs monthly
        monthly_equivalent_cents: 1658, // €199/12 months = ~€16.58
    },
    setup_assisted: {
        name: 'setup_assisted' as const,
        display_name: 'Setup Assistito',
        price_cents: 4990,
        price_display: '€49,90',
        duration_days: 0, // One-time, no duration
        stripe_price_id: process.env.VITE_STRIPE_SETUP_PRICE_ID || null,
    },
    test_1eur: {
        name: 'test_1eur' as const,
        display_name: 'Test 1€ (Super Admin)',
        price_cents: 100,
        price_display: '€1,00',
        duration_days: 30,
        stripe_price_id: process.env.VITE_STRIPE_TEST_1EUR_PRICE_ID || null,
    },
    unlimited: {
        name: 'unlimited' as const,
        display_name: 'Unlimited (Admin/Promo)',
        price_cents: 0,
        price_display: '€0',
        duration_days: 36500, // 100 years
        stripe_price_id: null,
    },
    affiliate: {
        name: 'affiliate' as const,
        display_name: 'Affiliate',
        price_cents: 0,
        price_display: '€0',
        duration_days: 36500, // 100 years
        stripe_price_id: null,
    },
} as const;

/**
 * Subscription Feature Checks
 */
export const FEATURE_FLAGS = {
    trial: {
        qr_code: true,
        directory: true,
        analytics: true,
        priority_support: false,
    },
    monthly: {
        qr_code: true,
        directory: true,
        analytics: true,
        priority_support: true,
    },
    yearly: {
        qr_code: true,
        directory: true,
        analytics: true,
        priority_support: true,
    },
} as const;

/**
 * Subscription Status Messages (i18n keys)
 */
export const SUBSCRIPTION_MESSAGES = {
    active: 'subscription.status.active',
    trial_ending_soon: 'subscription.status.trial_ending', // <7 days
    expired: 'subscription.status.expired',
    past_due: 'subscription.status.past_due',
    grace_period: 'subscription.status.grace_period',
} as const;

/**
 * Subscription Banner Types (for UI)
 */
export type SubscriptionBannerType =
    | 'success'  // Active subscription
    | 'warning'  // Expiring soon
    | 'error'    // Expired/Past due
    | 'info';    // Grace period

/**
 * Helper function return types
 */
export interface SubscriptionCheckResult {
    hasAccess: boolean;
    status: SubscriptionStatus | null;
    daysRemaining: number | null;
    expiresAt: string | null;
    bannerType: SubscriptionBannerType | null;
    message: string;
    planName: SubscriptionPlanName | null;
    gracePeriodStartedAt: string | null;
    isDeferredBilling?: boolean; // true = abbonamento confermato ma addebito futuro (acquisto durante trial)
    billingProvider?: BillingProvider; // 'stripe' | 'apple' | 'manual' — da RPC get_restaurant_billing_status
    renewsAutomatically?: boolean; // true = si rinnova da solo (Stripe/Apple, rinnovo attivo): la data è un rinnovo, non una scadenza
}

/**
 * Stripe Webhook Event Types we handle
 */
export type StripeWebhookEventType =
    | 'checkout.session.completed'
    | 'customer.subscription.updated'
    | 'customer.subscription.deleted'
    | 'invoice.paid'
    | 'invoice.payment_failed';

/**
 * Stripe Checkout Session Metadata
 */
export interface StripeCheckoutMetadata {
    restaurant_id: string;
    plan_name: SubscriptionPlanName;
    user_email: string;
}
